/**
 * Nordsee Direkt – Hauptnavigation (Off-Canvas mit Untermenü)
 * Steuerung für ndnav.html · Vue 2 · ersetzt die bisherige Navbar-Steuerung.
 *
 * Abwärtskompatibel: navbarToggle, favCount, goSearch, mailto und
 * emailAddress bleiben erhalten, falls andere Templates sie noch nutzen.
 */
import {
	isBlank
}
from 'voUtils/tools.js';

import {
	favCount, onFavUpdate
}
from 'voUtils/FavStore.js';

const SCROLL_THRESHOLD = 20;
const BODY_LOCK_CLASS = 'ndnav-body-locked';
const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default {

	data: {
		name: undefined,
		menuOpen: false,
		scrolled: false,
		solidMode: true,
		openSubmenu: null
	},

	computed: {
		mailto: function () {
			return `mailto:${this.emailAddress}`;
		},
		emailAddress: function () {
			return (VOFFICE.contactEmail || ['post', 'nordsee-direkt.com']).join('@');
		},
		headerClasses: function () {
			return {
				'is-scrolled': this.scrolled,
				'is-solid': this.solidMode || this.menuOpen
			};
		},
		phoneLink: function () {
			if (VOFFICE.phoneLink) {
				var el = document.getElementById('preloader');

				window.location.href = VOFFICE.phoneLink;
				if (el) {
					el.style.display = "none";
				}
			}
		},
		mailtoLink: function () {
			if (VOFFICE.mailtoLink) {
				var el = document.getElementById('preloader');

				window.location.href = VOFFICE.mailtoLink;
				if (el) {
					el.style.display = "none";
				}
			}
		}
	},

	mounted: function () {
		onFavUpdate(() => {
			this.$forceUpdate();
		});

		const root = this.$refs.ndnav;
		this.solidMode = !(root && root.getAttribute('data-ndnav-mode') === 'transparent');

		this._ndnavScrollTicking = false;
		this._ndnavLastFocus = null;
		this._ndnavBodyPadding = '';

		window.addEventListener('scroll', this.onWindowScroll, {
			passive: true
		});
		document.addEventListener('keydown', this.onDocumentKeydown);

		this.updateScrolled();
	},

	beforeDestroy: function () {
		window.removeEventListener('scroll', this.onWindowScroll);
		document.removeEventListener('keydown', this.onDocumentKeydown);
		this.unlockBody();
	},

	methods: {

		/* ---------- Menü ---------- */

		openMenu: function () {
			if (this.menuOpen) {
				return;
			}
			this._ndnavLastFocus = document.activeElement;
			this.menuOpen = true;
			this.lockBody();

			this.$nextTick(() => {
				const closeBtn = this.$refs.ndnavClose;
				if (closeBtn) {
					closeBtn.focus();
				}
			});
		},

		closeMenu: function () {
			if (!this.menuOpen) {
				return;
			}
			this.menuOpen = false;
			this.openSubmenu = null;
			this.unlockBody();

			const target = this._ndnavLastFocus || this.$refs.ndnavToggle;
			if (target && typeof target.focus === 'function') {
				target.focus();
			}
			this._ndnavLastFocus = null;
		},

		toggleMenu: function () {
			if (this.menuOpen) {
				this.closeMenu();
			} else {
				this.openMenu();
			}
		},

		// Alias für bestehende Templates, die noch navbarToggle aufrufen
		navbarToggle: function () {
			this.toggleMenu();
		},

		/* ---------- Untermenüs ---------- */

		toggleSubmenu: function (key) {
			this.openSubmenu = (this.openSubmenu === key) ? null : key;
		},

		isSubmenuOpen: function (key) {
			return this.openSubmenu === key;
		},

		/* ---------- Scroll-Zustand des Headers ---------- */

		onWindowScroll: function () {
			if (this._ndnavScrollTicking) {
				return;
			}
			this._ndnavScrollTicking = true;
			window.requestAnimationFrame(() => {
				this.updateScrolled();
				this._ndnavScrollTicking = false;
			});
		},

		updateScrolled: function () {
			const y = window.pageYOffset || document.documentElement.scrollTop || 0;
			this.scrolled = y > SCROLL_THRESHOLD;
		},

		/* ---------- Tastatur: Escape & Fokusfalle ---------- */

		onDocumentKeydown: function (e) {
			if (!this.menuOpen) {
				return;
			}
			if (e.key === 'Escape' || e.key === 'Esc') {
				e.preventDefault();
				this.closeMenu();
			} else if (e.key === 'Tab') {
				this.trapFocus(e);
			}
		},

		trapFocus: function (e) {
			const panel = this.$refs.ndnavPanel;
			if (!panel) {
				return;
			}
			const focusables = Array.prototype.filter.call(
				panel.querySelectorAll(FOCUSABLE_SELECTOR),
				function (el) {
					return el.offsetParent !== null && window.getComputedStyle(el).visibility !== 'hidden';
				}
			);
			if (!focusables.length) {
				return;
			}
			const first = focusables[0];
			const last = focusables[focusables.length - 1];

			if (!panel.contains(document.activeElement)) {
				e.preventDefault();
				first.focus();
			} else if (e.shiftKey && document.activeElement === first) {
				e.preventDefault();
				last.focus();
			} else if (!e.shiftKey && document.activeElement === last) {
				e.preventDefault();
				first.focus();
			}
		},

		/* ---------- Scroll-Sperre ohne Layoutsprung ---------- */

		lockBody: function () {
			const body = document.body;
			const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
			this._ndnavBodyPadding = body.style.paddingRight;
			if (scrollbarWidth > 0) {
				const current = parseFloat(window.getComputedStyle(body).paddingRight) || 0;
				body.style.paddingRight = (current + scrollbarWidth) + 'px';
			}
			body.classList.add(BODY_LOCK_CLASS);
		},

		unlockBody: function () {
			const body = document.body;
			if (!body.classList.contains(BODY_LOCK_CLASS)) {
				return;
			}
			body.classList.remove(BODY_LOCK_CLASS);
			body.style.paddingRight = this._ndnavBodyPadding || '';
		},

		/* ---------- Bestehende Funktionen ---------- */

		favCount: favCount,

		goSearch: function () {
			if (isBlank(this.name)) {
				window.location.href = 's';
			} else {
				window.location.href = 's#?name=' + encodeURIComponent(this.name);
			}
		}
	}
};